import { generateNumbers } from "@/utils/math";
import {
  PREVIEW_SCENE_LANE_X_SCALE,
  PREVIEW_SCENE_ROAD_HALF_WIDTH_M,
  PREVIEW_SCENE_TRACK_X_SCALE,
} from "@/constants/previewSceneDisplay";
import type {
  ObstacleInfo,
  ObstacleType,
  PreviewObstacleLane,
} from "../vehiclePerception/vehiclePerceptionTypes";

// ========== 预瞄信息页图表基础常量（自实现，不依赖 vehicleLeveling） ==========

export type PlayStatus = "play" | "pause" | "stop";

export const LENGTH = 60; // 绘制时间 60s（1分钟）
export const LENGTH_SECOND = 20; // 播放时可见窗口 20s

export const decreasingSequence = generateNumbers(LENGTH);
export const decreasingSequenceSecond = generateNumbers(LENGTH_SECOND);
export const PERCENTAGE = Number(
  Math.floor((LENGTH_SECOND / LENGTH) * 10000) / 100
);

/** 垂向加速度单系列配置 */
function optionVertSeries() {
  return [
    {
      id: "MSG_SusIMUAccVert",
      name: "垂向加速度/(g)",
      color: "#10b981",
      yAxisIndex: 0,
      lineStyle: { width: 4 },
    },
  ];
}

/** 垂向加速度 Y 轴（左轴） */
function getVertYaxis($q: { dark: { isActive: boolean } }) {
  const lineColor = $q.dark.isActive ? "#bdbdbd" : "#032f2b";
  const textColor = "#bdbdbd";
  const lineType = [2, 6];
  const yAxisOption: any = {
    type: "value",
    nameTextStyle: { fontSize: 16, fontWeight: "bold" },
    minorSplitLine: {
      show: false,
      lineStyle: { color: lineColor, type: lineType, width: 1 },
    },
    splitLine: {
      show: true,
      lineStyle: { color: textColor, type: lineType, width: 1 },
    },
    axisLine: { lineStyle: { color: lineColor, type: "solid", width: 2 } },
    axisLabel: { fontSize: 22, fontWeight: "bold", color: lineColor },
  };
  return [{ name: "垂向加速度/(g)", max: 3, min: -3, ...yAxisOption }];
}

const dataZoomOption: any = {
  height: 30,
  fillerColor: "#8e96a1",
  borderRadius: 5,
  moveHandleSize: 10,
  moveHandleStyle: { color: "#8e96a1" },
  brushStyle: {
    color: "#5aac5d",
    opacity: 0.7,
    borderWidth: 2,
    borderColor: "#5aac5d",
  },
  handleIcon:
    "path://M12 2C17.52 2 22 6.48 22 12S17.52 22 12 22 2 17.52 2 12 6.48 2 12 2zm0 20C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10z",
  handleSize: 50,
  handleStyle: {
    color: "rgba(0,0,0,1)",
    borderColor: "#8e96a1",
    borderWidth: 0,
    backgroundColor: "#8e96a1",
    opacity: 0.7,
  },
  backgroundColor: "rgba(0,0,0,0.0)",
};

function getACUDataZoom() {
  return [
    {
      id: "dataZoomX",
      type: "slider",
      xAxisIndex: [0],
      filterMode: "none",
      ...dataZoomOption,
      bottom: 10,
      moveHandleSize: 0,
      start: 66.67,
      end: 100,
      zoomLock: true,
      realtime: true,
      preventDefaultMouseMove: false,
      throttle: 100,
      brushSelect: false,
    },
    {
      type: "inside",
      xAxisIndex: [0],
      zoomOnMouseWheel: true,
      moveOnMouseMove: true,
      moveOnMouseWheel: false,
    },
  ];
}

/**
 * 根据当前位置、航向角和距离计算目标物经纬度
 * 使用平面近似：沿航向角方向投影 distanceMeter 米后的新坐标
 */
export function calculateBumpPosition(
  currentLat: number,
  currentLng: number,
  headingAngle: number,
  distanceMeter: number
): { latitude: number; longitude: number } {
  const radians = (headingAngle * Math.PI) / 180;
  const earthRadius = 6371000; // 地球半径 m
  // 纬度方向位移（北向为正）
  const deltaLat = (distanceMeter * Math.cos(radians)) / earthRadius;
  const newLat = currentLat + (deltaLat * 180) / Math.PI;
  // 经度方向位移（东向为正），需按纬度修正
  const deltaLng =
    (distanceMeter * Math.sin(radians)) /
    (earthRadius * Math.cos((currentLat * Math.PI) / 180));
  const newLng = currentLng + (deltaLng * 180) / Math.PI;
  return { latitude: newLat, longitude: newLng };
}

/** 预瞄路面特征颜色映射（仅 label + color，散点图用） */
export const previewFeatureMap: Record<
  number,
  { label: string; color: string }
> = {
  0: { label: "无", color: "#9e9e9e" },
  1: { label: "减速带", color: "#ff7043" },
  2: { label: "井盖", color: "#29b6f6" },
  3: { label: "破损路面", color: "#ef5350" },
  4: { label: "接缝", color: "#7e57c2" },
  5: { label: "上坡", color: "#66bb6a" },
  6: { label: "下坡", color: "#26a69a" },
  7: { label: "长波路", color: "#ab47bc" },
  8: { label: "连续减速带", color: "#fdd835" },
  9: { label: "凸起(预瞄)", color: "#ff9800" },
  10: { label: "凹陷(预瞄)", color: "#5c6bc0" },
  11: { label: "凸起(地图)", color: "#ff9800" },
  12: { label: "凹陷(地图)", color: "#5c6bc0" },
};

const DEFAULT_COLOR = "#2979ff";
const DISTANCE_THRESHOLD_MM = 1000;
/** 轴距 mm：RL/RR 原始距离减此后与前轴参考系下的 FL/FR 距离可比 */
const PREVIEW_WHEELBASE_MM = 3100;
/** 同侧前/后轮纵向距离容差（mm），对应需求 50cm */
const PREVIEW_WHEEL_PAIR_MATCH_MM = 500;
const LENGTH_THRESHOLD = 10;

/** 3D 横向车道 x（车体右为正）；随路面半宽、车长缩放与 vehiclePerceptionScene 一致 */
const PREVIEW_LANE_X_CLAMP = PREVIEW_SCENE_ROAD_HALF_WIDTH_M;
const PREVIEW_LANE_X: Record<PreviewObstacleLane, number> = {
  left: -3 * PREVIEW_SCENE_LANE_X_SCALE,
  center: 0,
  right: 3 * PREVIEW_SCENE_LANE_X_SCALE,
};

/** 左/右车道井盖横向（m）：轮迹随车长缩放（历史 ±0.95 @ 车长 3.5m） */
const PREVIEW_MANHOLE_SIDE_LANE_X: Record<"left" | "right", number> = {
  left: -0.95 * PREVIEW_SCENE_TRACK_X_SCALE,
  right: 0.95 * PREVIEW_SCENE_TRACK_X_SCALE,
};

/** 单侧预瞄槽位（dist 对后轮已为前轴等效距离 mm） */
interface PreviewSlot {
  dist: number;
  feat: number;
  hght: number;
  lengh: number;
}

/**
 * 左/右一对（同轴 FL/FR 或 RL/RR）：同特征且距离差 ≤1m 则合并为一条（取平均）
 */
function resolveSidePair(
  left: PreviewSlot | null,
  right: PreviewSlot | null
): PreviewSlot[] {
  if (!left && !right) return [];
  if (left && !right) return [left];
  if (!left && right) return [right];
  const l = left as PreviewSlot;
  const r = right as PreviewSlot;
  if (
    l.feat === r.feat &&
    Math.abs(l.dist - r.dist) <= DISTANCE_THRESHOLD_MM
  ) {
    return [
      {
        dist: (l.dist + r.dist) / 2,
        feat: l.feat,
        hght: (l.hght + r.hght) / 2,
        lengh: (l.lengh + r.lengh) / 2,
      },
    ];
  }
  return [l, r];
}

function averagePreviewCluster(cluster: PreviewSlot[]): PreviewSlot {
  const n = cluster.length;
  const feat = cluster[0].feat;
  const dist = cluster.reduce((s, c) => s + c.dist, 0) / n;
  const hght = cluster.reduce((s, c) => s + c.hght, 0) / n;
  const lengh = cluster.reduce((s, c) => s + c.lengh, 0) / n;
  return { dist, feat, hght, lengh };
}

/**
 * 同 feat 内按 dist 升序链式聚类：相邻 dist 差 ≤1m 归为一簇，簇内取平均
 */
function clusterSlotsByFeatAndDistance(slots: PreviewSlot[]): PreviewSlot[] {
  const byFeat = new Map<number, PreviewSlot[]>();
  for (const s of slots) {
    const list = byFeat.get(s.feat) ?? [];
    list.push(s);
    byFeat.set(s.feat, list);
  }
  const out: PreviewSlot[] = [];
  for (const group of byFeat.values()) {
    group.sort((a, b) => a.dist - b.dist);
    let cluster: PreviewSlot[] = [];
    for (const s of group) {
      if (cluster.length === 0) {
        cluster = [s];
        continue;
      }
      const last = cluster[cluster.length - 1];
      if (s.dist - last.dist <= DISTANCE_THRESHOLD_MM) {
        cluster.push(s);
      } else {
        out.push(averagePreviewCluster(cluster));
        cluster = [s];
      }
    }
    if (cluster.length > 0) {
      out.push(averagePreviewCluster(cluster));
    }
  }
  return out;
}

/** 距离自车 10cm 以内视为「已到达」目标物 */
export const DIST_NEAR_VEHICLE_MM = 100;

/** 经纬度相似阈值（约 10cm 量级） */
const POSITION_NEAR_THRESHOLD = 1e-6;

/** 航向角相似阈值（视为同一目标） */
const HEADING_SIMILAR_THRESHOLD = 15;

/** 航向角偏差超过此值则从 history 删除（车辆已驶离该目标） */
const HEADING_DIFF_REMOVE_THRESHOLD = 30;

/** 将角度差归一化到 [-180, 180]，用于航向角比较 */
function normalizeAngleDiff(deg: number): number {
  while (deg > 180) deg -= 360;
  while (deg < -180) deg += 360;
  return deg;
}

/** 经纬度+航向角都接近则视为同一目标 */
function isSameTarget(
  a: DomianControl.PreviewTargetItem,
  b: DomianControl.PreviewTargetItem
): boolean {
  return (
    isPositionNear(a.latitude, a.longitude, b.latitude, b.longitude) &&
    Math.abs(normalizeAngleDiff(a.headingAngle - b.headingAngle)) <
      HEADING_SIMILAR_THRESHOLD
  );
}

/** 车辆航向与目标航向偏差 > 30° 时视为已驶离，可从 history 删除 */
function headingDiffersSignificantly(
  vehicleHeading: number,
  targetHeading: number
): boolean {
  return (
    Math.abs(normalizeAngleDiff(vehicleHeading - targetHeading)) >
    HEADING_DIFF_REMOVE_THRESHOLD
  );
}

/**
 * 合并 FL/FR/RL/RR 预瞄数据，过滤 feat===0，计算经纬度，返回有效目标物数组。
 * 前轮距离为原始 mm；后轮距离先减轴距 PREVIEW_WHEELBASE_MM 得到前轴等效距离再参与比较与落盘。
 * 每索引内先左右合并，再按同 feat、相邻 dist 差 ≤1m（排序后链式聚类）做全局合并，最后 seen 去重。
 */
export function processPreviewInfo(
  data: DomianControl.PreviewInfoNotifyData
): DomianControl.PreviewTargetItem[] {
  const lat = data.INS_Latitude ?? 40.1683718;
  const lng = data.INS_Longitude ?? 116.573723;
  const heading = data.INS_HeadingAngle ?? 0;

  const featFL = data.MSG_PCM_FwdRoadFeat_FL ?? [];
  const featFR = data.MSG_PCM_FwdRoadFeat_FR ?? [];
  const featRL = data.MSG_PCM_FwdRoadFeat_RL ?? [];
  const featRR = data.MSG_PCM_FwdRoadFeat_RR ?? [];
  const hghtFL = data.MSG_PCM_SusObjHght_FL ?? [];
  const hghtFR = data.MSG_PCM_SusObjHght_FR ?? [];
  const hghtRL = data.MSG_PCM_SusObjHght_RL ?? [];
  const hghtRR = data.MSG_PCM_SusObjHght_RR ?? [];
  const lenghFL = data.MSG_PCM_SusObjLengh_FL ?? [];
  const lenghFR = data.MSG_PCM_SusObjLengh_FR ?? [];
  const lenghRL = data.MSG_PCM_SusObjLengh_RL ?? [];
  const lenghRR = data.MSG_PCM_SusObjLengh_RR ?? [];
  const distFL = data.MSG_PCM_SusObjDist_FL ?? [];
  const distFR = data.MSG_PCM_SusObjDist_FR ?? [];
  const distRL = data.MSG_PCM_SusObjDist_RL ?? [];
  const distRR = data.MSG_PCM_SusObjDist_RR ?? [];

  const candidates: PreviewSlot[] = [];

  for (let i = 0; i < LENGTH_THRESHOLD; i++) {
    const featFi = featFL[i];
    const featFri = featFR[i];
    const distFi = distFL[i];
    const distFri = distFR[i];
    const frontL =
      featFi && featFi !== 0 && distFi != null
        ? {
            dist: distFi,
            feat: featFi,
            hght: hghtFL[i] ?? 0,
            lengh: lenghFL[i] ?? 0,
          }
        : null;
    const frontR =
      featFri && featFri !== 0 && distFri != null
        ? {
            dist: distFri,
            feat: featFri,
            hght: hghtFR[i] ?? 0,
            lengh: lenghFR[i] ?? 0,
          }
        : null;
    candidates.push(...resolveSidePair(frontL, frontR));

    const featRli = featRL[i];
    const featRri = featRR[i];
    const rawRL = distRL[i];
    const rawRR = distRR[i];
    const rearL =
      featRli &&
      featRli !== 0 &&
      rawRL != null &&
      rawRL - PREVIEW_WHEELBASE_MM > 0
        ? {
            dist: rawRL - PREVIEW_WHEELBASE_MM,
            feat: featRli,
            hght: hghtRL[i] ?? 0,
            lengh: lenghRL[i] ?? 0,
          }
        : null;
    const rearR =
      featRri &&
      featRri !== 0 &&
      rawRR != null &&
      rawRR - PREVIEW_WHEELBASE_MM > 0
        ? {
            dist: rawRR - PREVIEW_WHEELBASE_MM,
            feat: featRri,
            hght: hghtRR[i] ?? 0,
            lengh: lenghRR[i] ?? 0,
          }
        : null;
    candidates.push(...resolveSidePair(rearL, rearR));
  }

  const merged = clusterSlotsByFeatAndDistance(candidates);
  const seen = new Set<string>();
  const targets: DomianControl.PreviewTargetItem[] = [];

  for (const slot of merged) {
    const key = `${slot.dist}-${slot.feat}-${slot.hght}-${slot.lengh}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const pos = calculateBumpPosition(lat, lng, heading, slot.dist / 1000);
    targets.push({
      latitude: pos.latitude,
      longitude: pos.longitude,
      headingAngle: heading,
      dist: slot.dist,
      length: slot.lengh,
      height: slot.hght,
      feat: slot.feat,
    });
  }

  return targets;
}

/** 将有效目标物转为 ECharts markArea 数据（参考 WheelTrajectoryCard buildMarkAreaFromBounds） */
export function buildMarkAreaFromTargets(
  targets: DomianControl.PreviewTargetItem[]
): any[] {
  const markAreaData: any[] = [];
  const defaultColor = DEFAULT_COLOR;

  targets.forEach((t) => {
    const cfg = previewFeatureMap[t.feat];
    const color = cfg?.color ?? defaultColor;

    let fillColor: string = color;
    // 将 #xxx/#xxxxxx 转为 rgba(..., 0.12) 作为填充色
    if (color.startsWith("#") && (color.length === 7 || color.length === 4)) {
      const hex =
        color.length === 7
          ? color.slice(1)
          : color
              .slice(1)
              .split("")
              .map((ch) => ch + ch)
              .join("");
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      fillColor = `rgba(${r}, ${g}, ${b}, 0.12)`;
    }

    // markArea 矩形：X=距离，Y=高度（凸起/凹陷）
    const minX = t.dist;
    const maxX = t.dist + t.length;
    const minY = Math.min(0, t.height);
    const maxY = Math.max(0, t.height);

    markAreaData.push([
      {
        xAxis: minX,
        yAxis: minY,
        itemStyle: {
          color: fillColor,
          borderColor: color,
          borderWidth: 2,
          borderType: "solid",
        },
      },
      {
        xAxis: maxX,
        yAxis: maxY,
      },
    ]);
  });

  return markAreaData;
}

/** X 轴总点数（0–10m 范围内） */
const SCATTER_POINT_COUNT = 1000;

/** X 轴范围 mm */
const X_AXIS_MAX_MM = 10000;

/**
 * 判断两经纬度是否接近（约 10cm 量级）
 */
export function isPositionNear(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): boolean {
  return (
    Math.abs(lat1 - lat2) < POSITION_NEAR_THRESHOLD &&
    Math.abs(lng1 - lng2) < POSITION_NEAR_THRESHOLD
  );
}

/** 车辆位置与航向（用于合并去重判断） */
export interface VehiclePosition {
  latitude: number;
  longitude: number;
  headingAngle: number;
}

/**
 * 合并去重：根据 targets 更新 history
 * - 经纬度+航向角差不多 → 视为同一目标
 * - history 有、targets 无，且车辆航向与目标航向偏差较大 → 从 history 删除
 * - history 有、targets 无，航向差不多 → 保留
 * - targets 有、history 无 → 添加到 history
 */
export function mergeTargetsNearVehicle(
  targets: DomianControl.PreviewTargetItem[],
  history: DomianControl.PreviewTargetItem[],
  vehicle: VehiclePosition
): DomianControl.PreviewTargetItem[] {
  const result: DomianControl.PreviewTargetItem[] = [];

  // 第一步：遍历 history，决定保留或删除
  for (const h of history) {
    const hasMatchInTargets = targets.some((t) => isSameTarget(t, h));
    if (hasMatchInTargets) {
      result.push(h); // 本帧仍可见 → 保留
    } else if (
      headingDiffersSignificantly(vehicle.headingAngle, h.headingAngle)
    ) {
      continue; // 已驶离且航向偏差大 → 删除
    } else {
      result.push(h); // 航向接近，可能仍相关 → 保留
    }
  }

  // 第二步：targets 中有而 result 中无的 → 新增
  for (const t of targets) {
    const hasMatchInResult = result.some((r) => isSameTarget(t, r));
    if (!hasMatchInResult) {
      result.push(t);
    }
  }

  return result;
}

/**
 * 目标物范围内平滑高度：使用 (1-cos) 形成圆润凸起/凹陷
 * x 从 dist 到 dist+length，y 从 0 平滑过渡到 height 再回到 0
 * t∈[0,1] 时 (1-cos(2πt)) 从 0→1→0，乘 height/2 得到实际高度
 */
export function smoothHeightAt(
  x: number,
  dist: number,
  length: number,
  height: number
): number {
  if (length <= 0) return 0;
  const t = (x - dist) / length;
  if (t < 0 || t > 1) return 0;
  return (height / 2) * (1 - Math.cos(2 * Math.PI * t));
}

/** parseSeriesData 调用间隔 50ms */
const PARSE_INTERVAL_SEC = 0.05;

/** 用于在多次调用间追踪当前正在绘制的目标物 */
export interface CurrentDrawnTargetRef {
  current: DomianControl.PreviewTargetItem | null;
}

/**
 * 计算下一帧预瞄目标物高度值
 * - 若 passingTargetBuffer 有数据：取第一个并更新 buffer；若取的是最后一个，则从 history 删除该目标
 * - 否则若当前经纬度与 history 中某目标接近：生成曲线、取第一个、剩余入 buffer，并记录当前目标
 * - 否则返回 0
 */
const DEBUG_PREVIEW_TARGET = true; // 调试时设为 true

export function computeNextPreviewTargetHeight(
  curLat: number,
  curLng: number,
  vehSpdKmh: number,
  history: DomianControl.PreviewTargetItem[],
  passingTargetBuffer: number[],
  currentDrawnTargetRef: CurrentDrawnTargetRef
): { previewHeight: number; newBuffer: number[] } {
  if (DEBUG_PREVIEW_TARGET) {
    console.log("[computeNextPreviewTargetHeight] 入参",JSON.parse(JSON.stringify(history)), {
      curLat,
      curLng,
      vehSpdKmh,
      historyLen: history.length,
      bufferLen: passingTargetBuffer.length,
      hasCurrentTarget: !!currentDrawnTargetRef.current,
    });
  }

  // 分支 1：buffer 中还有未消费的点 → 取第一个
  if (passingTargetBuffer.length > 0) {
    const [h, ...rest] = passingTargetBuffer;
    if (DEBUG_PREVIEW_TARGET) {
      console.log("[computeNextPreviewTargetHeight] 分支1: 从 buffer 取点", {
        previewHeight: h,
        restLen: rest.length,
      });
    }
    // 若取的是最后一个点 → 画完，从 history 删除并清空 ref
    if (rest.length === 0 && currentDrawnTargetRef.current) {
      const idx = history.indexOf(currentDrawnTargetRef.current);
      if (idx >= 0) history.splice(idx, 1);
      currentDrawnTargetRef.current = null;
      if (DEBUG_PREVIEW_TARGET) {
        console.log("[computeNextPreviewTargetHeight] 分支1: 画完，已从 history 删除");
      }
    }
    return { previewHeight: h ?? 0, newBuffer: rest };
  }

  // 分支 2：buffer 空，查找当前经纬度附近的目标
  const match = history.find((t) =>
    isPositionNear(curLat, curLng, t.latitude, t.longitude)
  );
  if (match) {
    currentDrawnTargetRef.current = match; // 记录正在绘制的目标
    const curve = generatePreviewTargetHeightCurve(match, vehSpdKmh);
    const [h, ...rest] = curve;
    if (DEBUG_PREVIEW_TARGET) {
      console.log("[computeNextPreviewTargetHeight] 分支2: 命中目标，生成曲线", {
        match: { lat: match.latitude, lng: match.longitude, height: match.height, length: match.length },
        curveLen: curve.length,
        previewHeight: h,
        restLen: rest.length,
      });
    }
    // 曲线只有 1 个点 → 直接画完并删除
    if (rest.length === 0) {
      const idx = history.indexOf(match);
      if (idx >= 0) history.splice(idx, 1);
      currentDrawnTargetRef.current = null;
      if (DEBUG_PREVIEW_TARGET) {
        console.log("[computeNextPreviewTargetHeight] 分支2: 曲线仅1点，画完并删除");
      }
    }
    return { previewHeight: h ?? 0, newBuffer: rest };
  }

  // 分支 3：无匹配目标 → 填 0
  return { previewHeight: 0, newBuffer: [] };
}

/**
 * 根据目标物 length 和车速 VehSpd(km/h) 生成平滑高度曲线
 * 曲线长度 = 通过目标物所需秒数 / 0.05，每 50ms 一个点
 */
export function generatePreviewTargetHeightCurve(
  target: DomianControl.PreviewTargetItem,
  vehSpdKmh: number
): number[] {
  // 车速 km/h → mm/s，最小 100 避免除零
  const speedMmPerSec = Math.max((vehSpdKmh ?? 0) * (1000000 / 3600), 100);
  // 通过目标物所需秒数 = 目标长度(mm) / 速度(mm/s)
  const durationSec = target.length / speedMmPerSec;
  // 点数 = 秒数 / 0.05（每 50ms 一个点）
  const pointCount = Math.max(1, Math.ceil(durationSec / PARSE_INTERVAL_SEC));
  const curve: number[] = [];
  for (let i = 0; i < pointCount; i++) {
    const x = i * PARSE_INTERVAL_SEC * speedMmPerSec; // 已行驶距离 mm
    // height 原始为 mm，转为 m 以与散点图 Y 轴一致（0.2、0.4 等）
    curve.push(smoothHeightAt(x, 0, target.length, target.height) / 1000);
  }
  return curve;
}

/**
 * 将有效目标物转为散点数据：固定 1000 个点沿 X 轴均匀分布
 * - 目标物范围内：y 按 (1-cos) 曲线平滑过渡，形成圆润凸起/凹陷
 * - 其他位置：y = 0，默认颜色
 */
export function buildScatterDataFromTargets(
  targets: DomianControl.PreviewTargetItem[]
): { value: [number, number]; itemStyle?: { color: string } }[] {
  const defaultColor = DEFAULT_COLOR;
  const step = X_AXIS_MAX_MM / (SCATTER_POINT_COUNT - 1); // X 轴 0~10000mm 均匀 1000 点

  const points: { value: [number, number]; itemStyle?: { color: string } }[] =
    [];

  for (let i = 0; i < SCATTER_POINT_COUNT; i++) {
    const x = Math.round(i * step);
    let y = 0;
    let color = defaultColor;

    // 检查 x 是否落在某目标物范围内 [dist, dist+length]
    for (const t of targets) {
      const start = t.dist;
      const end = t.dist + t.length;
      if (x >= start && x <= end) {
        y = smoothHeightAt(x, t.dist, t.length, t.height);
        const cfg = previewFeatureMap[t.feat];
        color = cfg?.color ?? defaultColor;
        break;
      }
    }

    points.push({
      value: [x, y],
      itemStyle: { color },
    });
  }

  return points;
}

// ========== 预瞄信息页图表配置（垂向加速度 + 预瞄目标物 双轴） ==========

/** 垂向加速度 + 预瞄目标物 双系列 tooltip */
export function getVertPreviewTargetTooltip() {
  return {
    show: true,
    trigger: "axis" as const,
    axisPointer: { type: "cross" as const, lineStyle: { width: 2 } },
    textStyle: { fontSize: 16 },
    formatter: (params: any[]) => {
      const axisValue = params[0]?.axisValue ?? "";
      let html = `<div style="font-size: 16px; font-weight: bold;">${axisValue}</div>`;
      params?.forEach?.((param: any) => {
        if (param.value === undefined) return;
        if (param.seriesType === "line") {
          const suffix = param.seriesIndex === 0 ? " g" : " m";
          html += `<div style="display: flex; align-items: center; font-size: 16px; margin-top: 5px;">
            <span style="margin-right: 8px;">${param.seriesName}</span>: <span style="font-weight: bold;">${param.value}${suffix}</span>
          </div>`;
        }
      });
      return html;
    },
  };
}

/** 预瞄信息页 dataZoom（复用 ACU 配置） */
export function getVertPreviewTargetDataZoom() {
  return getACUDataZoom();
}

/** 预瞄信息页 grid（右侧留空给第二 Y 轴） */
export function getVertPreviewTargetGrid() {
  return { top: 50, left: 70, right: 100, bottom: 80 };
}

/** 预瞄信息页双 Y 轴：左=垂向加速度，右=目标物高度（单位 m，与散点图一致） */
export function getVertPreviewTargetYaxis($q: { dark: { isActive: boolean } }) {
  const lineColor = $q.dark.isActive ? "#bdbdbd" : "#032f2b";
  return [
    ...getVertYaxis($q),
    {
      type: "value" as const,
      name: "目标物高度/m",
      max: 0.3,
      min: -0.3,
      nameTextStyle: { fontSize: 16, fontWeight: "bold" },
      splitLine: { show: false },
      axisLine: { lineStyle: { color: lineColor, type: "solid", width: 2 } },
      axisLabel: { fontSize: 22, fontWeight: "bold", color: lineColor },
    },
  ];
}

/** 预瞄信息页默认系列数据 */
export function getPreviewInfoDefaultSeriesData() {
  return {
    SusIMUAccVert: [] as number[],
    PreviewTargetHeight: [] as number[],
  };
}

/** 垂向加速度 + 预瞄目标物 双系列配置 */
export function getOptionsVertWithPreviewTarget() {
  return [
    ...optionVertSeries(),
    {
      id: "PreviewTargetHeight",
      name: "目标物高度/(m)",
      color: "#ff9800",
      yAxisIndex: 1,
      lineStyle: { width: 4, type: "dashed" as const },
    },
  ];
}

/** SOA 预瞄：减速带 / 井盖（与 previewinfo_soa 0x1 / 0x2 一致） */
export const PREVIEW_FEAT_SPEED_BUMP = 1;
export const PREVIEW_FEAT_MANHOLE = 2;

export function normalizePreviewFeatCode (feat: number): number {
  return feat & 0xff;
}

function enuMetersFromLatLng (
  lat0: number,
  lng0: number,
  lat1: number,
  lng1: number
): { east: number; north: number } {
  const R = 6371000;
  const dLat = ((lat1 - lat0) * Math.PI) / 180;
  const dLng = ((lng1 - lng0) * Math.PI) / 180;
  const north = dLat * R;
  const east = dLng * R * Math.cos((lat0 * Math.PI) / 180);
  return { east, north };
}

/** 与 calculateBumpPosition 一致：航向角下前向 = 北*cos + 东*sin，右向垂直于前向 */
function vehicleForwardRight (
  east: number,
  north: number,
  headingDeg: number
): { forward: number; right: number } {
  const h = (headingDeg * Math.PI) / 180;
  const fN = Math.cos(h);
  const fE = Math.sin(h);
  const rN = Math.sin(h);
  const rE = -Math.cos(h);
  return {
    forward: north * fN + east * fE,
    right: north * rN + east * rE,
  };
}

/**
 * 将 processPreviewInfo 结果转为 3D 场景障碍物（仅减速带、井盖）
 */
export function previewTargetsToSceneObstacles (
  targets: DomianControl.PreviewTargetItem[],
  vehicleLat: number,
  vehicleLng: number,
  vehicleHeadingDeg: number
): ObstacleInfo[] {
  const raw: ObstacleInfo[] = [];
  for (const t of targets) {
    const code = normalizePreviewFeatCode(t.feat);
    if (code !== PREVIEW_FEAT_SPEED_BUMP && code !== PREVIEW_FEAT_MANHOLE) {
      continue;
    }
    const type: ObstacleType =
      code === PREVIEW_FEAT_SPEED_BUMP ? "speedBump" : "manhole";
    const distM = Math.max(0, t.dist / 1000);
    const { east, north } = enuMetersFromLatLng(
      vehicleLat,
      vehicleLng,
      t.latitude,
      t.longitude
    );
    const { forward, right } = vehicleForwardRight(
      east,
      north,
      vehicleHeadingDeg
    );
    const x = Math.max(
      -PREVIEW_LANE_X_CLAMP,
      Math.min(PREVIEW_LANE_X_CLAMP, right)
    );
    const z = -forward;
    raw.push({
      id: 0,
      type,
      x,
      z,
      distance: Math.round(distM),
    });
  }
  raw.sort((a, b) => a.distance - b.distance);
  raw.forEach((o, i) => {
    o.id = i;
  });
  return raw;
}

function previewFeatToObstacleType (feat: number): ObstacleType | null {
  const code = normalizePreviewFeatCode(feat);
  if (code === PREVIEW_FEAT_SPEED_BUMP) {
    return "speedBump";
  }
  if (code === PREVIEW_FEAT_MANHOLE) {
    return "manhole";
  }
  return null;
}

/**
 * 与 FL/FR 纵向配对用的后轮距离（mm）：先按「后轴原始 − 轴距」折算；
 * 若与 dFront 差仍大、但与 rawRear 差 <50cm，则认定 rawRear 已与前轮同参照（实车常见）。
 */
function matchedRearEquivMm (
  dFront: number,
  rawRear: number
): number | null {
  if (!(rawRear > 0)) {
    return null;
  }
  const adj = rawRear - PREVIEW_WHEELBASE_MM;
  const okAdj =
    adj > 0 && Math.abs(dFront - adj) < PREVIEW_WHEEL_PAIR_MATCH_MM;
  const okSameFrame =
    Math.abs(dFront - rawRear) < PREVIEW_WHEEL_PAIR_MATCH_MM;
  if (okAdj) {
    return adj;
  }
  if (okSameFrame) {
    return rawRear;
  }
  return null;
}

function pushSceneObstacle (
  out: ObstacleInfo[],
  lane: PreviewObstacleLane,
  feat: number,
  distMmAvg: number
): void {
  const type = previewFeatToObstacleType(feat);
  if (!type) {
    return;
  }
  const distM = Math.max(0, distMmAvg / 1000);
  let x0 = PREVIEW_LANE_X[lane];
  if (type === "manhole" && (lane === "left" || lane === "right")) {
    x0 = PREVIEW_MANHOLE_SIDE_LANE_X[lane];
  }
  const x = Math.max(
    -PREVIEW_LANE_X_CLAMP,
    Math.min(PREVIEW_LANE_X_CLAMP, x0)
  );
  out.push({
    id: 0,
    type,
    x,
    z: -distM,
    distance: Math.round(distM),
    lane,
  });
}

/**
 * 预瞄 3D/HUD：按同一数组 index 做 FL↔RL、FR↔RR 前后轮配对（轴距折算、50cm 容差）。
 * 后轮距离可能为「后轴量纲需减轴距」或与 FL 已同参照，见 matchedRearEquivMm。
 * 双侧均成立且 FL/FR 同类型且纵向差<50cm → 仅一条居中；否则左/右可各一条。
 */
export function buildPreviewSceneObstaclesFromWheelPairs (
  data: DomianControl.PreviewInfoNotifyData
): ObstacleInfo[] {
  const featFL = data.MSG_PCM_FwdRoadFeat_FL ?? [];
  const featFR = data.MSG_PCM_FwdRoadFeat_FR ?? [];
  const featRL = data.MSG_PCM_FwdRoadFeat_RL ?? [];
  const featRR = data.MSG_PCM_FwdRoadFeat_RR ?? [];
  const distFL = data.MSG_PCM_SusObjDist_FL ?? [];
  const distFR = data.MSG_PCM_SusObjDist_FR ?? [];
  const distRL = data.MSG_PCM_SusObjDist_RL ?? [];
  const distRR = data.MSG_PCM_SusObjDist_RR ?? [];

  const raw: ObstacleInfo[] = [];

  for (let i = 0; i < LENGTH_THRESHOLD; i++) {
    const fFL = featFL[i];
    const fFR = featFR[i];
    const fRL = featRL[i];
    const fRR = featRR[i];
    const rawFL = distFL[i];
    const rawFR = distFR[i];
    const rawRL = distRL[i];
    const rawRR = distRR[i];

    const dFL =
      fFL != null && fFL !== 0 && rawFL != null ? Number(rawFL) : null;
    const dFR =
      fFR != null && fFR !== 0 && rawFR != null ? Number(rawFR) : null;

    const dRLEquiv =
      fFL != null &&
      fFL !== 0 &&
      fRL != null &&
      fRL !== 0 &&
      fFL === fRL &&
      dFL != null &&
      rawRL != null
        ? matchedRearEquivMm(dFL, Number(rawRL))
        : null;
    const dRREquiv =
      fFR != null &&
      fFR !== 0 &&
      fRR != null &&
      fRR !== 0 &&
      fFR === fRR &&
      dFR != null &&
      rawRR != null
        ? matchedRearEquivMm(dFR, Number(rawRR))
        : null;

    const leftOk = dRLEquiv != null;
    const rightOk = dRREquiv != null;

    const centerOk =
      leftOk &&
      rightOk &&
      fFL === fFR &&
      dFL != null &&
      dFR != null &&
      Math.abs(dFL - dFR) < PREVIEW_WHEEL_PAIR_MATCH_MM;

    if (centerOk && dRLEquiv != null && dRREquiv != null) {
      const distMmAvg = (dFL + dFR + dRLEquiv + dRREquiv) / 4;
      pushSceneObstacle(raw, "center", fFL, distMmAvg);
      continue;
    }
    if (leftOk && dFL != null && dRLEquiv != null) {
      pushSceneObstacle(raw, "left", fFL, (dFL + dRLEquiv) / 2);
    }
    if (rightOk && dFR != null && dRREquiv != null) {
      pushSceneObstacle(raw, "right", fFR, (dFR + dRREquiv) / 2);
    }
  }

  raw.sort((a, b) => a.distance - b.distance);
  raw.forEach((o, idx) => {
    o.id = idx;
  });
  return raw;
}

/** CarProperty 一帧数据 → 3D/HUD 用障碍物列表（轮对配对逻辑，非 processPreviewInfo 聚类） */
export function buildPreviewSceneObstacles (
  data: DomianControl.PreviewInfoNotifyData
): ObstacleInfo[] {
  return buildPreviewSceneObstaclesFromWheelPairs(data);
}

// ---------- 预瞄 3D：单侧车道需连续多帧非空确认；空帧不参与计数；连续空帧过多则丢弃 ----------

/** 单侧（左/右）需累计多少帧「非空且同侧」才输出 */
export const PREVIEW_SINGLE_SIDE_CONFIRM_NON_EMPTY_FRAMES = 5;

/** 连续多少帧无匹配则认为目标消失（不再保留上一帧） */
export const PREVIEW_OBSTACLE_MAX_EMPTY_STREAK_FRAMES = 2;

/** 与已有 track 匹配的最大纵向距离差（m） */
const PREVIEW_OBSTACLE_TRACK_MATCH_DIST_M = 3;

interface PreviewSceneObstacleTrack {
  type: ObstacleType;
  lastDistance: number;
  emptyStreak: number;
  pendingSide: PreviewObstacleLane | null;
  /** 同 pendingSide 的连续非空命中次数（中间空帧不增加、不重置，直至 emptyStreak 过大删轨） */
  singleSideNonEmptyCount: number;
  singleSideConfirmed: boolean;
  lastEmitted: ObstacleInfo | null;
}

function sortObstaclesByDistanceAssignIds (list: ObstacleInfo[]): ObstacleInfo[] {
  const sorted = [...list].sort((a, b) => a.distance - b.distance);
  return sorted.map((o, idx) => ({ ...o, id: idx }));
}

/**
 * 预瞄 3D：对 `buildPreviewSceneObstacles` 每帧原始障碍物列表做跨帧时间稳定（track 匹配、左/右连续帧确认、空帧短 hold、单侧未确认时沿用上一帧防闪空）。
 * 在 `PreviewInfo.vue` 中长期复用同一实例，每帧调用 `push(raw)`。
 * 详细行为与流程图见同目录 `previewinfo.md`。
 */
export function createPreviewSceneObstacleStabilizer (): {
  push: (raw: ObstacleInfo[]) => ObstacleInfo[];
} {
  let seq = 1;
  const tracks = new Map<string, PreviewSceneObstacleTrack>();

  function newTrackId (): string {
    return `preview_ob_${seq++}`;
  }

  return {
    push (rawList: ObstacleInfo[]): ObstacleInfo[] {
      const out: ObstacleInfo[] = [];
      const usedTrackIds = new Set<string>();
      const pairs: Array<{ trackId: string; raw: ObstacleInfo }> = [];

      const order = rawList
        .map((_, i) => i)
        .sort((a, b) => rawList[a].distance - rawList[b].distance);

      for (const i of order) {
        const raw = rawList[i];
        let best: { tid: string; diff: number } | null = null;
        for (const [tid, tr] of tracks) {
          if (usedTrackIds.has(tid)) {
            continue;
          }
          if (tr.type !== raw.type) {
            continue;
          }
          const diff = Math.abs(tr.lastDistance - raw.distance);
          if (
            diff <= PREVIEW_OBSTACLE_TRACK_MATCH_DIST_M &&
            (!best || diff < best.diff)
          ) {
            best = { tid, diff };
          }
        }
        if (best) {
          usedTrackIds.add(best.tid);
          pairs.push({ trackId: best.tid, raw });
        } else {
          const tid = newTrackId();
          tracks.set(tid, {
            type: raw.type,
            lastDistance: raw.distance,
            emptyStreak: 0,
            pendingSide: null,
            singleSideNonEmptyCount: 0,
            singleSideConfirmed: false,
            lastEmitted: null,
          });
          usedTrackIds.add(tid);
          pairs.push({ trackId: tid, raw });
        }
      }

      for (const { trackId, raw } of pairs) {
        const tr = tracks.get(trackId);
        if (!tr) {
          continue;
        }
        tr.emptyStreak = 0;
        tr.lastDistance = raw.distance;

        const lane: PreviewObstacleLane = raw.lane ?? "center";

        if (lane === "center") {
          tr.pendingSide = null;
          tr.singleSideNonEmptyCount = 0;
          tr.singleSideConfirmed = false;
          out.push(raw);
          tr.lastEmitted = raw;
          continue;
        }

        if (tr.singleSideConfirmed) {
          out.push(raw);
          tr.lastEmitted = raw;
          continue;
        }

        if (tr.pendingSide === null) {
          tr.pendingSide = lane;
          tr.singleSideNonEmptyCount = 1;
        } else if (lane !== tr.pendingSide) {
          tr.pendingSide = lane;
          tr.singleSideNonEmptyCount = 1;
        } else {
          tr.singleSideNonEmptyCount += 1;
        }

        if (
          tr.singleSideNonEmptyCount >= PREVIEW_SINGLE_SIDE_CONFIRM_NON_EMPTY_FRAMES
        ) {
          tr.singleSideConfirmed = true;
          out.push(raw);
          tr.lastEmitted = raw;
        } else if (tr.lastEmitted != null) {
          // 已匹配但单侧未凑满：仍输出上一帧稳定结果，避免 raw 非空时 UI 闪空（计数不变）
          out.push({ ...tr.lastEmitted });
        }
      }

      for (const tid of [...tracks.keys()]) {
        if (usedTrackIds.has(tid)) {
          continue;
        }
        const tr = tracks.get(tid);
        if (!tr) {
          continue;
        }
        tr.emptyStreak += 1;
        if (tr.emptyStreak > PREVIEW_OBSTACLE_MAX_EMPTY_STREAK_FRAMES) {
          tracks.delete(tid);
          continue;
        }
        const le = tr.lastEmitted;
        if (!le) {
          continue;
        }
        const holdCenter = le.lane === "center" || le.lane === undefined;
        if (holdCenter || tr.singleSideConfirmed) {
          out.push({ ...le });
        }
      }

      return sortObstaclesByDistanceAssignIds(out);
    },
  };
}
